import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import store, { persistor } from "./store/index";
import reportWebVitals from "./reportWebVitals";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from "react-router-dom";
import SellerDashboard from "./features/SellerFlow/dashboard/SellerDashboard";
import ViewCampaigns from "./features/SellerFlow/dashboard/view-campaigns/ViewCampaigns";
import RfpRequests from "./features/SellerFlow/rfp-requests/RfpRequests";
import CampaignDetailPage from "./features/SellerFlow/dashboard/campaign-detailpage/CampaignDetailPage";
import PersonalDetails from "./components/common/personal-details/PersonalDetails";
import { Provider, useSelector } from "react-redux";
import HomePage from "./features/HomePage/Homepage";
import AboutPage from "./components/layout/about/AboutPage";
import LoginPage from "./features/LoginPage/LoginPage";
import SignupPage from "./features/Signup/SignupPage";
import CampaignPayments from "./features/BuyerFlow/payments-pages/CampaignPayments";

import ContactPage from "./components/layout/contact/ContactPage";
import ProductsPage from "./components/layout/products/ProductsPage";
import PrivacyAndPolicies from "./components/layout/privacypolicies/PrivacyAndPolicies";
import CampaignDetailsPage from "./features/BuyerFlow/campaigndetailspage/CampaignDetailsPage";
import MyCampaignsPage from "./features/BuyerFlow/mycampaigns/MyCampaignsPage";
import BuyerPayments from "./features/BuyerFlow/payments/Payments";
import MobileProductDetails from "./components/common/product-details/mobile/ProductDetailsVersion";
import CreateNewPassword from "./features/LoginPage/createnewpassword/CreateNewPassword";
import ViewCart from "./features/BuyerFlow/viewcart/ViewCart";
import RfpMobileVersion from "./components/modal/rfp-request-modal/RfpVersion";
import MyBillboard from "./features/SellerFlow/dashboard/billboards/MyBillboard";
import Payments from "./features/SellerFlow/payments/Payments";
import BuyerDashboard from "./features/BuyerFlow/BuyerDashboard/BuyerDashboard";
import PaymentSuccess from "./features/BuyerFlow/payments-pages/payment-success/PaymentSuccess";
import CheckPaymentVersion from "./features/BuyerFlow/payments-pages/check-payment/CheckPaymentVersion";
import ProtectedRoute from "./protectedRoute";
import Notifications from "./components/common/notifications/Notifications";
import { PersistGate } from "redux-persist/integration/react";
import TermsRequest from "./features/SellerFlow/terms-requests/TermsRequestPage";
import NotFoundPage from "./components/common/error-handling-page/NotFoundPage";
import { Buffer } from "buffer";
import ExportExcel from "./components/modal/export-excel-modal/ExportExcel";
import ErrorFallback from "./components/common/global-error-handling/ErrorFallback";
import DocuSignSuccessPage from "./features/BuyerFlow/payments-pages/docusign-success/DocusignSuccessPage";
import ChatCommunication from "./components/common/chat-communication/ChatCommunication";
import SellerChatCommunication from "./components/common/chat-communication/SellerChatCommunication";
import ProductDetailPage from "./components/common/product-details/web/ProductDetailPage";
import { AdProvider } from "./features/Ads/adContext/adContext";
import ProductChatListPage from "./components/common/seller-chat-components/ProductChatListPage";
import ProductChatingPad from "./components/common/seller-chat-components/ProductChatingPad";
import LeadGenerationForm from "./features/BuyerFlow/lead-generation-form/LeadGenerationForm";
import LeadGenerationSuccess from "./features/BuyerFlow/lead-generation-form/LeadGenerationSuccess";
import Messages from "./features/BuyerFlow/payments-pages/messages/Messages";
window.Buffer = Buffer;

// ✅ Error Fallback UI
function GlobalErrorFallback({ error, resetError }) {
  return (
    <div style={{ padding: 20, textAlign: "center", color: "red" }}>
      <h2>Something went wrong 😢</h2>
      <pre>{error?.message}</pre>
      <button onClick={resetError}>Reload App</button>
    </div>
  );
}

// ✅ Error Boundary (class-based)
class GlobalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("🔥 Global error caught:", error, errorInfo);
    // send to monitoring service like Sentry/Datadog here
  }

  render() {
    if (this.state.hasError) {
      return (
        <GlobalErrorFallback
          error={this.state.error}
          resetError={this.resetError}
        />
      );
    }
    return this.props.children;
  }
}

// ✅ Catch async & global JS errors (outside React)
window.onerror = (msg, url, lineNo, columnNo, error) => {
  console.error("🌍 Global window error:", {
    msg,
    url,
    lineNo,
    columnNo,
    error,
  });
  return true; // prevent app crash
};

window.onunhandledrejection = (event) => {
  console.error("🌍 Unhandled Promise rejection:", event.reason);
  return true; // prevent crash
};
const lastChatUrl = sessionStorage.getItem("chatPostLogin");

const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <AdProvider>
        {" "}
        <App />
      </AdProvider>
    ),
    errorElement: <ErrorFallback />,
    children: [
      { path: "/", element: <Navigate to="/home" /> },
      { path: "home", element: <HomePage /> },
      { path: "about", element: <AboutPage /> },
      { path: "login", element: <LoginPage /> },
      { path: "signup", element: <SignupPage /> },
      // { path: "pricing", element: <PricingPage /> },
      { path: "reset-password/:token", element: <CreateNewPassword /> },
      { path: "contact", element: <ContactPage /> },
      {
        element: <ProtectedRoute restrictForSeller={true} />,
        children: [{ path: "products", element: <ProductsPage /> }],
      },
      { path: "productsdetails", element: <ProductDetailPage /> },
      { path: "privacy-policy", element: <PrivacyAndPolicies /> },
      { path: "rfp", element: <RfpMobileVersion /> },
      { path: "not-found", element: <NotFoundPage /> },
      { path: "*", element: <Navigate to="/not-found" /> },
      { path: "get-in-touch", element: <LeadGenerationForm /> },
      { path: "get-in-touch/success", element: <LeadGenerationSuccess /> },
      {
        path: "buyer",
        element: <ProtectedRoute role="basic" />,
        children: [
          { path: "dashboard", element: <BuyerDashboard /> },
          { path: "products", element: <ProductsPage /> },
          { path: "campaigndetails", element: <CampaignDetailsPage /> },
          {
            path: "campaigndetails/export-data",
            element: <ExportExcel />,
          },
          { path: "mycampaigns", element: <MyCampaignsPage /> },
          { path: "viewcart", element: <ViewCart /> },
          { path: "viewproduct", element: <MobileProductDetails /> },
          { path: "personaldetails", element: <PersonalDetails /> },
          { path: "payments", element: <BuyerPayments /> },
          { path: "campaign-payment", element: <CampaignPayments /> },
          { path: "productsdetails", element: <ProductDetailPage /> },
          { path: "payment-success", element: <PaymentSuccess /> },
          { path: "io-signing/success", element: <DocuSignSuccessPage /> },
          { path: "check-upload", element: <CheckPaymentVersion /> },
          { path: "notifications", element: <Notifications /> },
          { path: "privacy-policy", element: <PrivacyAndPolicies /> },
          { path: "contact", element: <ContactPage /> },
          {
            path: "chat/:campaignId/:bookingId",
            element: <ChatCommunication />,
          },
          { path: "not-found", element: <NotFoundPage /> },
          {
            path: "*",
            element: lastChatUrl ? (
              <Navigate to="/buyer/not-found" />
            ) : (
              <Navigate to="/buyer/products" />
            ),
          },
          { path: "get-in-touch", element: <LeadGenerationForm /> },
          { path: "messages", element: <Messages /> },
        ],
      },
      {
        path: "seller",
        element: <ProtectedRoute role="owner" />,
        children: [
          {
            path: "dashboard",
            element: <SellerDashboard />,
          },
          {
            path: "viewCampaigns",
            element: <ViewCampaigns />,
          },
          {
            path: "rfpRequests",
            element: <RfpRequests />,
          },
          {
            path: "viewCampaigns/campaignDetails/:campaignId",
            element: <CampaignDetailPage />,
          },
          {
            path: "campaigndetails/export-data",
            element: <ExportExcel />,
          },
          {
            path: "myProducts",
            element: <MyBillboard />,
          },
          { path: "viewproduct", element: <MobileProductDetails /> },
          {
            path: "personaldetails",
            element: <PersonalDetails />,
          },
          {
            path: "payments",
            element: <Payments />,
          },
          { path: "campaign-chat-list", element: <SellerChatCommunication /> },
          {
            path: "products-chat-list/:campaignId",
            element: <ProductChatListPage />,
          },
          {
            path: "products-chat-list/:campaignId/:bookingId",
            element: <ProductChatingPad />,
          },

          { path: "notifications", element: <Notifications /> },
          { path: "terms-requests", element: <TermsRequest /> },
          { path: "not-found", element: <NotFoundPage /> },
          { path: "*", element: <Navigate to="/seller/dashboard" /> },
          {
            path: "messages",
            element: <Messages />,
          },
        ],
      },
    ],
  },
]);

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor}>
      <GlobalErrorBoundary>
        <RouterProvider router={router} />
      </GlobalErrorBoundary>
    </PersistGate>
  </Provider>,
);

reportWebVitals();
